/**
 * FlyerDeals API — typed wrapper around `GET /api/flyerdeals` (T12.0.15 Step 5).
 *
 * Single endpoint: the response is already pre-grouped by store and
 * pre-sorted (favorite-first across stores; non-expired then expired
 * within each store). Clients render verbatim — no client-side sort.
 *
 * `include_expired` is forwarded as a query string param. The server
 * parses strictly (only the literal string `"true"` is truthy), so the
 * client passes the boolean coerced via `String(...)`.
 */

import type { HttpClient } from './client';
import {
  type FlyerDealsResponse,
  flyerDealsResponseSchema,
  type DealsSearchResponse,
  dealsSearchResponseSchema,
  type DealsSearchSuggestionsResponse,
  dealsSearchSuggestionsResponseSchema,
} from '../types/FlyerDeals';

const API_BASE = '/api/flyerdeals';

export interface FetchFlyerDealsOptions {
  includeExpired?: boolean;
}

export interface SearchFlyerDealsOptions {
  q: string;
  storeIds?: string[];
  /** Lowercase server opt-out: search the bar without synonym or typo rewrite. */
  rewrite?: 'off';
}

export class FlyerDealsApi {
  constructor(private readonly http: HttpClient) {}

  fetchAll(options: FetchFlyerDealsOptions = {}): Promise<FlyerDealsResponse> {
    return this.http.get(API_BASE, flyerDealsResponseSchema, {
      include_expired: options.includeExpired ? 'true' : 'false',
    });
  }

  /**
   * T55.3 — fit-ranked deals search. `q` is the raw bar text (bar grammar:
   * `word` / `-word`); `storeIds` (optional) scopes to a CSV subset of the
   * household's selected stores, defaulting to all of them when omitted.
   */
  search(options: SearchFlyerDealsOptions): Promise<DealsSearchResponse> {
    return this.http.get(`${API_BASE}/search`, dealsSearchResponseSchema, {
      q: options.q,
      ...(options.rewrite ? { rewrite: options.rewrite } : {}),
      ...(options.storeIds && options.storeIds.length > 0
        ? { store_ids: options.storeIds.join(',') }
        : {}),
    });
  }

  searchSuggestions(q: string): Promise<DealsSearchSuggestionsResponse> {
    return this.http.get(`${API_BASE}/search/suggestions`, dealsSearchSuggestionsResponseSchema, { q });
  }

  /**
   * T55.3 Deliverable 4 — "Not what you're looking for?" confirmed-miss
   * report. Fire-and-forget from the surface's perspective: the tap logs
   * immediately; an optional note may ride the same call. Never throws in a
   * way that should block the search flow — callers should not await this
   * on the critical UI path (fire it, don't gate on it).
   */
  reportMiss(query: string, note?: string): Promise<void> {
    return this.http.postVoid(`${API_BASE}/search/miss-report`, {
      query,
      ...(note ? { note } : {}),
    });
  }

  /**
   * T55.9 Deliverable 1 — "refinement_applied" capture. Fire-and-forget,
   * mirroring `reportMiss`: callers should not await this on the critical UI
   * path. `kind` distinguishes a chip tap from a typed correction-undo
   * (Details: `refinement_op` jsonb — `{kind: chip|typed, action:
   * include|exclude, word}`).
   */
  reportRefinement(query: string, word: string, exclude: boolean, kind: 'chip' | 'typed' = 'chip'): Promise<void> {
    return this.http.postVoid(`${API_BASE}/search/refinement`, {
      query,
      word,
      exclude,
      kind,
    });
  }

  /**
   * T55.9 Deliverable 1 — "search_abandoned" capture: a search session's
   * no-pick final state (the bar clearing after a search was active).
   * Fire-and-forget, same contract as `reportMiss`/`reportRefinement`.
   */
  reportAbandon(query: string): Promise<void> {
    return this.http.postVoid(`${API_BASE}/search/abandon`, { query });
  }

  /**
   * T55.9 known-gap fix — "result_picked" capture: the surface calls this
   * once when a search result card is opened (the pick affordance T55.9
   * shipped without, since the surface had no tap-to-open result at the
   * time). Fire-and-forget, same contract as `reportMiss`/`reportAbandon`.
   */
  reportResultPicked(query: string, systemProductId: string): Promise<void> {
    return this.http.postVoid(`${API_BASE}/search/result-picked`, {
      query,
      systemProductId,
    });
  }
}
